/* Catalog-driven controls: one control per property, derived from the catalog
 * def. Closed sets (enum) render as a segmented switch; booleans as a toggle;
 * numbers/strings as inputs; scalar lists as a line editor. Structural props
 * (children / object data / actions) show an explanatory note — their sample
 * content is visible in the preview + JSON. */
import React from "react";
import type { CatalogComponentDef, CatalogPropertyDef } from "../preview/engine/types";

const TYPE_LABEL: Record<string, string> = {
  BoundString: "text", BoundBoolean: "true / false", BoundNumber: "number",
  BoundList: "list", ComponentRef: "component", ComponentList: "components",
  ActionDescriptor: "action",
};

function isScalarList(v: unknown): v is (string | number)[] {
  return Array.isArray(v) && v.every((x) => typeof x === "string" || typeof x === "number");
}

function noteFor(t: string): string {
  if (t === "ComponentRef") return "Holds one component — sample content shown in the preview.";
  if (t === "ComponentList") return "Holds child components — sample content shown in the preview.";
  if (t === "ActionDescriptor") return "Runs a workflow when triggered.";
  if (t === "BoundList") return "Structured data — sample shown in the preview.";
  return "—";
}

export function ControlsPanel({
  def, values, onChange,
}: {
  def: CatalogComponentDef | undefined;
  values: Record<string, unknown>;
  onChange: (name: string, value: unknown) => void;
}) {
  const props = Object.entries(def?.properties ?? {}) as [string, CatalogPropertyDef][];
  return (
    <div className="gd-controls">
      {props.map(([name, pdef]) => (
        <Control key={name} name={name} pdef={pdef} value={values[name]} onChange={(v) => onChange(name, v)} />
      ))}
    </div>
  );
}

function Control({
  name, pdef, value, onChange,
}: {
  name: string;
  pdef: CatalogPropertyDef;
  value: unknown;
  onChange: (v: unknown) => void;
}) {
  let control: React.ReactNode;

  if (pdef.enum) {
    control = (
      <div className="gd-seg">
        {pdef.enum.map((opt) => (
          <button
            key={opt}
            type="button"
            className={"gd-seg-btn" + (value === opt ? " active" : "")}
            onClick={() => onChange(opt)}
          >
            {opt}
          </button>
        ))}
      </div>
    );
  } else if (pdef.type === "BoundBoolean") {
    control = (
      <button
        type="button"
        role="switch"
        aria-checked={!!value}
        className={"gd-toggle" + (value ? " on" : "")}
        onClick={() => onChange(!value)}
      >
        <span className="gd-toggle-dot" />
      </button>
    );
  } else if (pdef.type === "BoundNumber") {
    control = (
      <input
        className="gd-input"
        type="number"
        value={value === undefined || value === null ? "" : (value as number)}
        onChange={(e) => onChange(e.target.value === "" ? undefined : Number(e.target.value))}
      />
    );
  } else if (pdef.type === "BoundString") {
    control = (
      <input
        className="gd-input"
        type="text"
        value={(value as string) ?? ""}
        onChange={(e) => onChange(e.target.value)}
      />
    );
  } else if (pdef.type === "BoundList" && isScalarList(value)) {
    control = (
      <textarea
        className="gd-input gd-textarea"
        rows={Math.min(6, Math.max(2, value.length))}
        value={value.join("\n")}
        onChange={(e) => onChange(e.target.value.split("\n").map((s) => s.trim()).filter(Boolean))}
      />
    );
  } else {
    control = <div className="gd-ctl-note">{noteFor(pdef.type)}</div>;
  }

  return (
    <div className="gd-ctl">
      <div className="gd-ctl-head">
        <span className="gd-ctl-name">{name}{pdef.required ? <span className="gd-req">*</span> : null}</span>
        <span className="gd-ctl-type">{pdef.enum ? pdef.enum.join(" · ") : TYPE_LABEL[pdef.type] ?? pdef.type}</span>
      </div>
      {control}
    </div>
  );
}
